import { alpha, type Theme } from '@mui/material/styles'

/**
 * Theme-derived primary color helpers for sx props.
 * Use these instead of hardcoded rgba values so every tint
 * automatically follows the primary color from design-tokens.
 *
 * Usage:
 *   import { primary } from '../../theme/palette'
 *   <Box sx={{ bgcolor: primary.selected }} />
 */

function pa(lightAlpha: number, darkAlpha: number) {
  return (t: Theme) =>
    alpha(t.palette.primary.main, t.palette.mode === 'light' ? lightAlpha : darkAlpha)
}

export const primary = {
  // ── Backgrounds ────────────────────────────────────────────────
  /** Very faint tint — used for hover on non-selected items */
  hover:          pa(0.04, 0.08),
  /** Selected row / card background */
  selected:       pa(0.06, 0.12),
  /** Hovered while selected */
  selectedHover:  pa(0.09, 0.18),
  /** Soft chip / badge / pill background */
  soft:           pa(0.08, 0.16),
  /** Calendar cell / dialog highlight */
  highlight:      pa(0.10, 0.22),
  highlightHover: pa(0.14, 0.28),

  // ── Shadows ────────────────────────────────────────────────────
  /** Primary CTA button glow */
  glow: (t: Theme) => `0 2px 8px -2px ${alpha(t.palette.primary.main, 0.45)}`,
  glowHover: (t: Theme) => `0 4px 14px -3px ${alpha(t.palette.primary.main, 0.55)}`,

  /** ViewSwitcher pill */
  pillBg: pa(0.10, 0.22),
  pillShadow: (t: Theme) =>
    t.palette.mode === 'light'
      ? `0 1px 3px ${alpha(t.palette.primary.main, 0.22)}, inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}`
      : `0 1px 3px rgba(0,0,0,0.32), inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.30)}`,
}
